import { useMemo, useState } from "react";
import {
  Entry,
  formatQuantity,
  isCounted,
  toNumber,
} from "../lib/entries";
import { DraftNumberInput } from "./DraftNumberInput";
import { AdjustmentsDisclosure } from "./AdjustmentsDisclosure";
import { SaveStatusChip, SaveStatus } from "./SaveStatusChip";

interface StoreCountModeProps {
  entries: Entry[];
  onSaveField: (entryId: string, field: string, raw: string) => void;
  onClose: () => void;
  locked: boolean;
  saveStatus: SaveStatus;
  onRetryFailedSaves: () => void;
}

export function StoreCountMode({
  entries,
  onSaveField,
  onClose,
  locked,
  saveStatus,
  onRetryFailedSaves,
}: StoreCountModeProps) {
  // Snapshot the work queue when count mode opens: uncounted items first;
  // if everything is already counted, walk through all items for review.
  const [queue] = useState<string[]>(() => {
    const uncounted = entries.filter((e) => !isCounted(e)).map((e) => e.id);
    return uncounted.length > 0 ? uncounted : entries.map((e) => e.id);
  });
  const [index, setIndex] = useState(0);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [sessionCounted, setSessionCounted] = useState(0);

  const entryById = useMemo(() => {
    const map = new Map<string, Entry>();
    entries.forEach((e) => map.set(e.id, e));
    return map;
  }, [entries]);

  const totalItems = entries.length;
  const countedItems = entries.filter(isCounted).length;
  const netVariance = entries.reduce(
    (sum, e) => sum + (toNumber(e.variance) ?? 0),
    0
  );

  const currentId = queue[index];
  const current = currentId ? entryById.get(currentId) : undefined;
  const done = index >= queue.length;

  const goNext = () => setIndex((i) => i + 1);
  const goPrev = () => {
    setIndex((i) => Math.max(0, i - 1));
  };

  const handleSaveNext = (raw: string) => {
    if (!current) return;
    onSaveField(current.id, "physicalCount", raw);
    setSkipped((prev) => {
      if (!prev.has(current.id)) return prev;
      const next = new Set(prev);
      next.delete(current.id);
      return next;
    });
    setSessionCounted((n) => n + 1);
    goNext();
  };

  const handleSkip = () => {
    if (current && !isCounted(current)) {
      setSkipped((prev) => new Set(prev).add(current.id));
    }
    goNext();
  };

  if (done || !current) {
    const skippedRemaining = queue.filter((id) => {
      const entry = entryById.get(id);
      return entry ? !isCounted(entry) : false;
    });

    return (
      <div className="count-mode">
        <header className="count-mode-header">
          <button type="button" className="count-mode-close" onClick={onClose}>
            Close
          </button>
          <p className="count-mode-position">Count complete</p>
          <div className="count-mode-status">
            <SaveStatusChip status={saveStatus} onRetry={onRetryFailedSaves} />
          </div>
        </header>

        <div className="count-mode-body">
          <div className="count-summary">
            <h2>Nice work</h2>
            <div className="count-summary-grid">
              <div className="metric-chip">
                <span>Counted this session</span>
                <strong>{sessionCounted}</strong>
              </div>
              <div className="metric-chip">
                <span>Still uncounted</span>
                <strong>{totalItems - countedItems}</strong>
              </div>
              <div className="metric-chip">
                <span>Day progress</span>
                <strong>
                  {countedItems}/{totalItems}
                </strong>
              </div>
              <div className="metric-chip">
                <span>Net variance</span>
                <strong
                  className={
                    netVariance < 0
                      ? "metric-negative"
                      : netVariance > 0
                        ? "metric-positive"
                        : "metric-neutral"
                  }
                >
                  {netVariance.toFixed(2)}
                </strong>
              </div>
            </div>

            {skippedRemaining.length > 0 && (
              <button
                type="button"
                className="count-mode-secondary"
                onClick={() => {
                  setIndex(queue.indexOf(skippedRemaining[0]));
                }}
              >
                Review {skippedRemaining.length} skipped{" "}
                {skippedRemaining.length === 1 ? "item" : "items"}
              </button>
            )}
            <button
              type="button"
              className="count-mode-primary"
              onClick={onClose}
            >
              Back to list
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="count-mode">
      <header className="count-mode-header">
        <button type="button" className="count-mode-close" onClick={onClose}>
          Close
        </button>
        <p className="count-mode-position">
          Item {index + 1} of {queue.length}
        </p>
        <div className="count-mode-status">
          <SaveStatusChip status={saveStatus} onRetry={onRetryFailedSaves} />
          <p className="count-mode-progress-label">
            {countedItems}/{totalItems} counted
          </p>
        </div>
      </header>

      <div className="progress-shell count-mode-progress">
        <span
          className={`progress-fill ${
            countedItems === totalItems ? "is-complete" : "is-mid"
          }`}
          style={{
            width: `${totalItems ? Math.round((countedItems / totalItems) * 100) : 0}%`,
          }}
        />
      </div>

      <div className="count-mode-body">
        <CountStep
          key={current.id}
          entry={current}
          locked={locked}
          skipped={skipped.has(current.id)}
          onSaveField={onSaveField}
          onSaveNext={handleSaveNext}
          onSkip={handleSkip}
          onPrev={index > 0 ? goPrev : undefined}
        />
      </div>
    </div>
  );
}

function CountStep({
  entry,
  locked,
  skipped,
  onSaveField,
  onSaveNext,
  onSkip,
  onPrev,
}: {
  entry: Entry;
  locked: boolean;
  skipped: boolean;
  onSaveField: (entryId: string, field: string, raw: string) => void;
  onSaveNext: (raw: string) => void;
  onSkip: () => void;
  onPrev?: () => void;
}) {
  const initial = toNumber(entry.physicalCount);
  const [draft, setDraft] = useState(initial === null ? "" : String(initial));

  return (
    <div className="count-step">
      <div className="count-step-item">
        <h2>{entry.name}</h2>
        <p className="table-item-subtitle">
          {entry.code ?? "No code"}
          {skipped ? " · skipped earlier" : ""}
        </p>
      </div>

      <div className="count-step-metrics">
        <div className="metric-chip">
          <span>Opening</span>
          <strong>{formatQuantity(entry.opening)}</strong>
        </div>
        <div className="metric-chip">
          <span>Closing</span>
          <strong>{formatQuantity(entry.closing)}</strong>
        </div>
      </div>

      <div className="count-step-secondary">
        <label>
          <span>Purchases</span>
          <DraftNumberInput
            value={entry.purchases}
            onSave={(raw) => onSaveField(entry.id, "purchases", raw)}
            className="numeric-input"
            disabled={locked}
          />
        </label>
        <label>
          <span>Sales</span>
          <DraftNumberInput
            value={entry.sales}
            onSave={(raw) => onSaveField(entry.id, "sales", raw)}
            className="numeric-input"
            disabled={locked}
          />
        </label>
      </div>

      <label className="count-step-physical">
        <span>Physical count</span>
        <input
          type="number"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !locked && draft.trim() !== "") {
              onSaveNext(draft);
            }
          }}
          onFocus={(e) => e.target.select()}
          placeholder="0"
          className="count-step-input"
          autoFocus
          disabled={locked}
        />
      </label>

      <AdjustmentsDisclosure
        entry={entry}
        onSaveField={onSaveField}
        disabled={locked}
      />

      <div className="count-step-actions">
        {onPrev && (
          <button
            type="button"
            className="count-mode-secondary"
            onClick={onPrev}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="count-mode-secondary"
          onClick={onSkip}
        >
          Skip
        </button>
        <button
          type="button"
          className="count-mode-primary"
          onClick={() => onSaveNext(draft)}
          disabled={locked || draft.trim() === ""}
        >
          Save &amp; next
        </button>
      </div>
    </div>
  );
}
